// ============================================================================
// Billing — клиентские и серверные хелперы для тарифов и лимитов
// ============================================================================
// Используется в:
// - Странице тарифов /settings/billing (показать карточки и текущее использование)
// - Server actions (проверка лимита перед созданием сотрудника/партнёра)
// - UI (показать счётчики «3 / 10 сотрудников» в шапке)
//
// ВАЖНО: дефолты (если миграция _apply_manually__billing_plans.sql не применена)
// — все возвращают «безлимит» чтобы не блокировать работу системы.

export interface SeatPackage {
  size: number              // сколько слотов в пакете (1, 5, 10, 25)
  discount: number          // скидка в процентах (0-100)
  label?: string            // отображаемое название
}

export type PlanAudience = 'forwarder' | 'importer' | 'all'

export interface SubscriptionPlan {
  id: string
  name: string
  description: string | null
  price_monthly: number
  price_yearly: number | null
  currency: string
  max_seats: number | null
  max_partners: number | null
  max_deals_per_month: number | null
  max_tenders_per_month: number | null
  max_landings: number | null
  max_storage_mb: number | null
  max_telegram_bots: number | null
  modules: Record<string, boolean>
  features: string[]
  // Локализованный контент (jsonb в БД): { ru, ro, en }. Fallback — description/features (RU).
  /** Имя по языкам · заход 806. Раньше имя было одно, русское, а описание
   *  и возможности уже переводились. */
  name_i18n?: Record<string, string> | null
  description_i18n?: Record<string, string> | null
  features_i18n?: Record<string, string[]> | null
  extra_seat_price: number | null
  seat_packages: SeatPackage[]
  is_active: boolean
  sort_order: number
  is_default: boolean
  audience?: PlanAudience              // forwarder | importer | all
}

export interface CompanyPlanInfo {
  plan: SubscriptionPlan | null
  seats_purchased: number              // купленные сверх лимита
  /** Личные лимиты компании (1140, 07.10): null — по тарифу. Нет — всё по тарифу. */
  overrides?: LimitOverrides
  plan_started_at: string | null
  plan_expires_at: string | null
  billing_period: 'monthly' | 'yearly'
  trial_ends_at: string | null
}

export interface CompanyUsage {
  seats: number                        // фактическое количество сотрудников
  partners: number
  deals_this_month: number
  tenders_this_month: number
  landings: number
  telegram_bots: number
}

export type LimitKey =
  | 'seats' | 'partners' | 'deals_per_month' | 'tenders_per_month'
  | 'landings' | 'telegram_bots'

// ── ЛИНИЯ ТАРИФОВ И ЛИЧНЫЕ ЛИМИТЫ — ОДНО ПРАВИЛО ДЛЯ ВСЕХ (1140, 07.10) ───────
// Тарифы двух линий: импортёр (тендеры) и экспедитор (сделки). Линию задаёт
// роль компании — так же, как при заведении демо. Личный лимит компании
// (договорились с клиентом) перекрывает лимит тарифа; пусто — по тарифу.
// Этим правилом пользуются ВСЕ: проверка при создании, учёт использования,
// экран «Тариф» клиента и карточка клиента в админке.
export type PlanLine = 'importer' | 'forwarder'

// Линия тарифов по роли компании: импортёр или организатор торгов — линия
// импортёра, остальные (экспедитор, перевозчик) — линия экспедитора.
export function planLineFor(c: { business_type?: string | null; company_role?: string | null }): PlanLine {
  return c.business_type === 'importer' || c.company_role === 'organizer' ? 'importer' : 'forwarder'
}

// Тарифы, которые можно выдать компании этой линии: включённые, своей линии
// или общие, по порядку витрины.
export function plansForLine<T extends Pick<SubscriptionPlan, 'is_active' | 'audience' | 'sort_order'>>(plans: T[], line: PlanLine): T[] {
  return plans
    .filter(p => p.is_active && (p.audience === line || p.audience === 'all' || !p.audience))
    .sort((a, b) => a.sort_order - b.sort_order)
}

/** Личные лимиты компании по ключам. null / нет ключа — по тарифу. */
export type LimitOverrides = Partial<Record<LimitKey, number | null>>

// Личные лимиты из строки компании (колонки companies.max_*).
export function overridesFromCompany(c: Record<string, unknown> | null | undefined): LimitOverrides {
  const n = (v: unknown) => (v === null || v === undefined || v === '' ? null : Number(v))
  if (!c) return {}
  return {
    seats: n(c.max_users), partners: n(c.max_partners),
    deals_per_month: n(c.max_deals), tenders_per_month: n(c.max_tenders),
  }
}

/** Действующий лимит: личный, если задан, иначе тарифа (с купленными местами).
 *  null = безлимит */
export function getEffectiveLimit(
  plan: SubscriptionPlan | null, key: LimitKey, seatsPurchased = 0, overrides: LimitOverrides = {},
): number | null {
  const own = overrides[key]
  if (own !== null && own !== undefined) return key === 'seats' ? own + seatsPurchased : own
  if (!plan) return null  // нет плана — безлимит (старая компания, ещё не применена миграция)

  switch (key) {
    case 'seats':
      if (plan.max_seats === null) return null
      return plan.max_seats + seatsPurchased
    case 'partners':            return plan.max_partners
    case 'deals_per_month':     return plan.max_deals_per_month
    case 'tenders_per_month':   return plan.max_tenders_per_month
    case 'landings':            return plan.max_landings
    case 'telegram_bots':       return plan.max_telegram_bots
  }
}

/** Текущее использование по ключу из объекта usage */
export function getCurrentUsage(usage: CompanyUsage, key: LimitKey): number {
  switch (key) {
    case 'seats':              return usage.seats
    case 'partners':           return usage.partners
    case 'deals_per_month':    return usage.deals_this_month
    case 'tenders_per_month':  return usage.tenders_this_month
    case 'landings':           return usage.landings
    case 'telegram_bots':      return usage.telegram_bots
  }
}

/** Проверяет можно ли создать ещё одну сущность. true = можно, false = лимит достигнут. */
export function canAddOne(
  plan: SubscriptionPlan | null,
  usage: CompanyUsage,
  key: LimitKey,
  seatsPurchased = 0,
  overrides: LimitOverrides = {},
): { allowed: boolean; current: number; limit: number | null; remaining: number | null } {
  const limit = getEffectiveLimit(plan, key, seatsPurchased, overrides)
  const current = getCurrentUsage(usage, key)
  if (limit === null) {
    return { allowed: true, current, limit: null, remaining: null }
  }
  const remaining = Math.max(0, limit - current)
  return {
    allowed: current < limit,
    current,
    limit,
    remaining,
  }
}

// 907: подписи лимитов и formatLimit убраны — их импортировал экран тарифа,
// но нигде не показывал. Лимиты на экране подписывает словарь settings.billing.

/** Цвет прогресса исходя из процента использования */
export function getUsageColor(current: number, limit: number | null): 'green' | 'amber' | 'red' {
  if (limit === null) return 'green'
  const pct = (current / limit) * 100
  if (pct >= 95) return 'red'
  if (pct >= 75) return 'amber'
  return 'green'
}

/** Проверяет включён ли модуль в тарифе */
export function isModuleEnabled(plan: SubscriptionPlan | null, moduleKey: string): boolean {
  if (!plan) return true  // нет плана — всё включено (для обратной совместимости)
  return plan.modules?.[moduleKey] === true
}

/** Дни до истечения тарифа (для триала или платного с фиксированным сроком) */
export function daysUntilExpiry(expiresAt: string | null | undefined): number | null {
  if (!expiresAt) return null
  const exp = new Date(expiresAt).getTime()
  const now = Date.now()
  return Math.ceil((exp - now) / (24 * 60 * 60 * 1000))
}

/**
 * Расчёт стоимости пакета сотрудников с учётом скидки.
 * Возвращает { totalMonthly, perSeatMonthly, savings }.
 */
export function calculatePackagePrice(
  pkg: SeatPackage,
  extraSeatPrice: number,
): { totalMonthly: number; perSeatMonthly: number; savings: number } {
  const basePrice = pkg.size * extraSeatPrice
  const discountAmount = (basePrice * pkg.discount) / 100
  const totalMonthly = basePrice - discountAmount
  const perSeatMonthly = totalMonthly / pkg.size
  return {
    totalMonthly: Math.round(totalMonthly * 100) / 100,
    perSeatMonthly: Math.round(perSeatMonthly * 100) / 100,
    savings: Math.round(discountAmount * 100) / 100,
  }
}

// ============================================================================
// ЛИМИТЫ ТАРИФА — ЧТО ПОКАЗАТЬ НА КАРТОЧКЕ.
// ----------------------------------------------------------------------------
// На карточках тарифа (кабинет и лендинг) были только слова возможностей, а
// сколько людей, тендеров и записей даёт тариф — нигде. Числа лежат в самом
// тарифе (subscription_plans.max_*), и именно по ним система ставит пределы:
// показываем их же, без второго источника.
//
// Одно правило на оба экрана. Порядок — по тому, о чём спрашивают первым:
// сколько людей, потом сколько главной работы линии.
// ============================================================================

export type PlanLimitKey =
  | 'seats' | 'tenders' | 'deals' | 'partners' | 'landings' | 'bots' | 'storage'

export interface PlanLimit {
  key: PlanLimitKey
  /** null — без ограничений. */
  value: number | null
}

/**
 * Лимиты тарифа для показа.
 *
 * Пусто в базе — без ограничений. Ноль у того, чего в линии нет (сделки у
 * тендерного тарифа, лендинги у бесплатного), не показываем: «0 сделок» у
 * импортёра читается как урезанность, хотя сделок в этой линии нет вовсе.
 * Людей показываем всегда — о них спрашивают первыми.
 */
export function planLimits(plan: Pick<SubscriptionPlan,
  'max_seats' | 'max_tenders_per_month' | 'max_deals_per_month' | 'max_partners'
  | 'max_landings' | 'max_telegram_bots' | 'max_storage_mb' | 'modules'>): PlanLimit[] {
  const mods = plan.modules ?? {}
  const out: PlanLimit[] = [{ key: 'seats', value: plan.max_seats }]
  const add = (key: PlanLimitKey, value: number | null, on = true) => {
    if (on && value !== 0) out.push({ key, value })
  }
  add('tenders', plan.max_tenders_per_month, mods.tenders !== false)
  add('deals', plan.max_deals_per_month, mods.transport !== false)
  add('partners', plan.max_partners)
  add('landings', plan.max_landings)
  add('bots', plan.max_telegram_bots)
  add('storage', plan.max_storage_mb)
  return out
}

/** Объём хранилища словами: до гигабайта — в МБ, дальше — в ГБ. */
export function storageSize(mb: number): { value: number; unit: 'mb' | 'gb' } {
  return mb >= 1024
    ? { value: Math.round((mb / 1024) * 10) / 10, unit: 'gb' }
    : { value: mb, unit: 'mb' }
}
